/**
 * Dragon Tiger — server-authoritative rules engine (2–6 players against the house, session game with chips).
 *
 * Rules implemented:
 *  - One 52-card deck, reshuffled before every round. Ace is low (1) and the king is high (13); suits never matter.
 *  - Each player bets on DRAGON, TIGER or TIE, in seat order, or sits the round out.
 *  - One card is dealt face up to the Dragon, then one to the Tiger. The higher card wins.
 *  - A winning Dragon or Tiger bet pays 1:1; a winning Tie bet pays `tiePays`:1 (11:1). When the round is a tie, Dragon and
 *    Tiger bets lose half of the stake (`tieRefund` percent comes back, 50 by default).
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export type DragonTigerSide = 'DRAGON' | 'TIGER' | 'TIE';

export interface DragonTigerConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  /** A winning Tie bet pays this many times the stake. */
  tiePays: number;
  /** Percent of a Dragon/Tiger stake that comes back when the round is a tie. */
  tieRefund: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type DragonTigerPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'HAND_END';
export type DragonTigerSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';

export interface DragonTigerSeat {
  playerId: string;
  chips: number;
  status: DragonTigerSeatStatus;
  side: DragonTigerSide | null;
  bet: number;
  /** What came back at the end of the round (stake plus winnings, the refund on a tie, or 0). */
  payout: number;
}

export interface DragonTigerResult {
  handNo: number;
  winner: DragonTigerSide;
  dragon: CardId;
  tiger: CardId;
  seats: { seat: number; side: DragonTigerSide; bet: number; payout: number; delta: number }[];
}

export interface DragonTigerState {
  config: DragonTigerConfig;
  seats: (DragonTigerSeat | null)[];
  phase: DragonTigerPhase;
  handNo: number;
  /** Seat to bet. */
  turn: number;
  deck: CardId[];
  dragon: CardId | null;
  tiger: CardId | null;
  winner: DragonTigerSide | null;
  lastResult: DragonTigerResult | null;
  handsPlayed: number;
}

export type DragonTigerAction = { type: 'bet'; side: DragonTigerSide; amount: number } | { type: 'sitOut' };

export type DragonTigerLegal = { kind: 'bet'; min: number; max: number; canSitOut: boolean } | { kind: 'none' };

export interface DragonTigerView {
  gameType: 'DRAGON_TIGER';
  config: DragonTigerConfig;
  seat: number | null;
  phase: DragonTigerPhase;
  handNo: number;
  turn: number;
  dragon: CardId | null;
  tiger: CardId | null;
  winner: DragonTigerSide | null;
  deckCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: DragonTigerSeatStatus; side: DragonTigerSide | null; bet: number; payout: number } | null)[];
  lastResult: DragonTigerResult | null;
}

const DEFAULTS: DragonTigerConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  tiePays: 11,
  tieRefund: 50,
  startDelayMs: 2000,
  dealDelayMs: 1100,
  handEndDelayMs: 4000,
};

/** Dragon Tiger card value: ace 1 (low) up to king 13. */
export const dragonTigerValue = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
};

function seatsWhere(s: DragonTigerState, pred: (p: DragonTigerSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: DragonTigerState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function advanceBetting(s: DragonTigerState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function startHand(s: DragonTigerState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = shuffle(buildDeck(), rng);
  s.dragon = null;
  s.tiger = null;
  s.winner = null;
  for (const p of s.seats) {
    if (!p) continue;
    p.side = null;
    p.bet = 0;
    p.payout = 0;
    p.status = p.chips >= s.config.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: DragonTigerState, events: EngineEvent[]): void {
  const d = dragonTigerValue(s.dragon!);
  const t = dragonTigerValue(s.tiger!);
  const winner: DragonTigerSide = d > t ? 'DRAGON' : t > d ? 'TIGER' : 'TIE';
  s.winner = winner;
  const cfg = s.config;
  const seats: DragonTigerResult['seats'] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET' || p.side === null) return;
    if (winner === 'TIE') p.payout = p.side === 'TIE' ? p.bet + p.bet * cfg.tiePays : Math.floor((p.bet * cfg.tieRefund) / 100);
    else p.payout = p.side === winner ? p.bet * 2 : 0;
    p.chips += p.payout;
    seats.push({ seat, side: p.side, bet: p.bet, payout: p.payout, delta: p.payout - p.bet });
  });
  s.lastResult = { handNo: s.handNo, winner, dragon: s.dragon!, tiger: s.tiger!, seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, winner, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const dragonTigerEngine: GameEngine<DragonTigerConfig, DragonTigerState, DragonTigerAction, DragonTigerView, DragonTigerLegal> = {
  gameType: 'DRAGON_TIGER',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('tiePays', c.tiePays, 1, 50);
    checkInt('tieRefund', c.tieRefund, 0, 100);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: DragonTigerState = {
      config,
      seats: Array<DragonTigerSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      deck: shuffle(buildDeck(), rng),
      dragon: null,
      tiger: null,
      winner: null,
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', side: null, bet: 0, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bet comes back.
      chips += p.bet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are coming forfeits the bet on the table.
      s.seats[seat] = null;
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut') return { type: 'sitOut' };
    if (raw.type === 'bet' && (raw.side === 'DRAGON' || raw.side === 'TIGER' || raw.side === 'TIE') && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) {
      return { type: 'bet', side: raw.side, amount: raw.amount };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Dragon Tiger action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING', 'INVALID_PHASE', 'Betting is closed');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (action.type === 'sitOut') {
      p.status = 'SIT_OUT';
      events.push({ type: 'sitOut', seat });
      advanceBetting(s, seat);
      return events;
    }
    const max = Math.min(s.config.maxBet, p.chips);
    assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
    p.chips -= action.amount;
    p.bet = action.amount;
    p.side = action.side;
    p.status = 'BET';
    events.push({ type: 'bet', seat, side: action.side, amount: action.amount });
    advanceBetting(s, seat);
    return events;
  },

  getActors(s) {
    return s.phase === 'BETTING' && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    if (s.phase !== 'BETTING' || seat !== s.turn || !s.seats[seat]) return { kind: 'none' };
    return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, s.seats[seat]!.chips), canSitOut: true };
  },

  getTimeoutAction() {
    return { type: 'sitOut' };
  },

  getView(s, seat) {
    return {
      gameType: 'DRAGON_TIGER',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      dragon: s.dragon,
      tiger: s.tiger,
      winner: s.winner,
      deckCount: s.deck.length,
      players: s.seats.map((p, i) => (p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, side: p.side, bet: p.bet, payout: p.payout } : null)),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      // The Dragon card first, then the Tiger card, one per step so the table can show the deal as it happens.
      if (s.deck.length < 2) s.deck = shuffle(buildDeck(), rng);
      if (s.dragon === null) {
        s.dragon = s.deck.pop()!;
        events.push({ type: 'cardDealt', to: 'DRAGON', card: s.dragon });
      } else {
        s.tiger = s.deck.pop()!;
        events.push({ type: 'cardDealt', to: 'TIGER', card: s.tiger });
        settle(s, events);
      }
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
